import { Inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import moment, { Duration } from 'moment';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Observable, of } from 'rxjs';
import { EscalationComponent } from '../../components/escalation/escalation.component';
import { EscalationModel } from '../../components/escalation/escalation.model';
import { ConfigConstants } from '../../config/config-constants';
import { NOTIFICATION_STATUS_CODE } from '../../enums/notification-status-code.enum';
import { NotificationDataInstanceToken, NotificationDataInterface } from '../../interfaces/notification-data.interface';
import { NotificationTimerModel } from '../../models/low-level/notification-timer.model';
import { NotificationModel } from '../../models/low-level/notification.model';
import { OdataEscalationService } from './odata-escalation.service';
import { catchError, mergeMap, take } from 'rxjs/operators';
import { AppNotificationType } from '../app-notification/app-notification.model';
import { CONNECTION_ERROR_CODES } from '../../enums/connection-error-codes.enum';
import { TranslateService } from '@ngx-translate/core';
import { AppNotificationService } from '../app-notification/app-notification.service';
import { SynchronizationInstanceToken, SynchronizationInterface } from '../../interfaces/synchronization.interface';
import { EscalationInterface } from '../../interfaces/escalation.interface';

@Injectable({ providedIn: 'root' })
export class EscalationService implements EscalationInterface {
    private escalationTimers: NotificationTimerModel[] = [];
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        @Inject(NotificationDataInstanceToken) private readonly notificationService: NotificationDataInterface,
        private readonly modalService: BsModalService,
        private readonly odataEscalationService: OdataEscalationService,
        private readonly translate: TranslateService,
        private readonly appNotificationService: AppNotificationService,
        @Inject(SynchronizationInstanceToken) protected readonly synchronizationService: SynchronizationInterface,
    ) {}

    /**
     * This function adds the timer based on the duration (i.e from OSR start time till now)
     * If the duration is more than say 4 hrs the event has to fire immediately
     * This functionality is only valid for MS type notifications.
     *
     * @param notification
     * @param durationPassed
     */
    public setTimerForEscalation(notification: NotificationModel, durationPassed: Duration): void {
        if (notification.notificationType !== ConfigConstants.NotificationTypes.Service) {
            return;
        }
        const timer =
            durationPassed.asHours() >= ConfigConstants.escalationTimeoutAlertTimingInHours
                ? 0
                : ConfigConstants.escalationTimeoutAlertTimingInHours - durationPassed.asHours(); // calc ulating the time difference

        const notificationTimer = new NotificationTimerModel(
            notification.identifier,
            window.setTimeout(
                () => this.showEscalationTimeoutModal(notification),
                timer *
                    ConfigConstants.timeMultiplier *
                    ConfigConstants.timeMultiplier *
                    ConfigConstants.milliSecToSecConversionFactor,
            ),
        );

        this.escalationTimers.push(notificationTimer);
    }

    /**
     * This function removes the already existing timer
     *
     * @param notification
     */
    public removeEscalationTimer(notification: NotificationModel): void {
        this.escalationTimers.forEach((notificationTimerModel) => {
            if (notificationTimerModel.notificationId.equalsTo(notification.identifier)) {
                // TODO : Use cancellation handler to cancel timer
                window.clearTimeout(notificationTimerModel.timer);
            }
        });
    }

    /**
     * This function checks if any notification of type Service (MS) is in Progress status
     * If yes, then it calculate the difference between the OSR start time and current time
     * and set the timer to raise the escalation alert
     */
    public checkEscalationRequired(): void {
        const notifications = this.notificationService.getAllNotificationsInProgress();

        notifications.forEach((notification) => {
            if (notification && notification.notificationType === ConfigConstants.NotificationTypes.Service) {
                // noti fication in progress might have the status code OSR or TRAV
                const status = this.notificationService.getStatus(notification);
                // chec ks if notification status is OSR
                const osrStatus = status.statusCode === NOTIFICATION_STATUS_CODE.OSR ? status : undefined;
                if (!osrStatus) {
                    return;
                }

                const isEscalated = this.notificationService.getEscalated(notification);
                if (isEscalated) {
                    return;
                }

                const timerExists = this.checkTimerAddedOnNotification(notification);
                if (timerExists) {
                    return;
                }

                const now = moment();
                const osrDuration = moment.duration(now.diff(osrStatus.startTime));
                this.setTimerForEscalation(notification, osrDuration);
            }
        });
    }

    public saveEscalationStatus(escalation: EscalationModel, notification: NotificationModel): Observable<void> {
        return this.odataEscalationService.saveEscalationStatus(escalation).pipe(
            take(1),
            mergeMap(() => this.notificationService.saveEscalated(notification, true)),
            mergeMap(() => this.synchronizationService.sendOfflineData()),
            catchError((error) => {
                this.log.error(
                    'saveEscalationStatus - error' + JSON.stringify(error, Object.getOwnPropertyNames(error)),
                );
                this.appNotificationService.setAppNotification(
                    AppNotificationType.ERROR,
                    error === CONNECTION_ERROR_CODES.OFFLINE_STORE_SAVE_ERROR
                        ? <string>this.translate.instant('OFFLINE_STORE_ERROR_TITLE')
                        : <string>this.translate.instant('ESCALATION_SUBMIT_ERROR_TITLE'),
                    error === CONNECTION_ERROR_CODES.OFFLINE_STORE_SAVE_ERROR
                        ? <string>this.translate.instant('OFFLINE_STORE_ERROR_BODY')
                        : <string>this.translate.instant('ESCALATION_SUBMIT_ERROR_BODY'),
                );
                return of(undefined);
            }),
        );
    }

    public showEscalationPopup(notification: NotificationModel): void {
        this.modalService.show(EscalationComponent, {
            backdrop: 'static',
            class: 'modal-dialog modal-fullscreen-lg-down',
            initialState: {
                notification,
            },
            ariaLabelledBy: 'escalation-modal-title',
        });
    }

    /**
     * This function checks if timer is already added for this notification.
     *
     * @param notification
     */
    private checkTimerAddedOnNotification(notification: NotificationModel): boolean {
        const notificationTimer = this.escalationTimers.filter((timer) =>
            timer.notificationId.equalsTo(notification.identifier),
        );
        return notificationTimer.length > 0;
    }

    /**
     * This function will open notification Escalation Timeout modal
     *
     * @param notification
     */
    private showEscalationTimeoutModal(notification: NotificationModel): void {
        const status = this.notificationService.getStatus(notification);

        if (status.statusCode !== NOTIFICATION_STATUS_CODE.OSR) {
            this.log.error(
                `EscalationService: showEscalationTimeoutModal: Tried to escalate notification ${notification.identifier.toString()} without OSR status!`,
            );
            return;
        }

        this.showEscalationPopup(notification);
    }
}
